import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft, Save, User, Briefcase, DollarSign, Phone, MapPin, Shield,
  FileText, Award, AlertTriangle, Clock, Plus, Trash2, CalendarClock, X
} from 'lucide-react';

export default function StaffDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [showWriteUpDialog, setShowWriteUpDialog] = useState(false);
  const [showRecognitionDialog, setShowRecognitionDialog] = useState(false);
  const [writeUpForm, setWriteUpForm] = useState({ type: 'verbal', description: '', date: new Date().toISOString().split('T')[0] });
  const [recognitionForm, setRecognitionForm] = useState({ type: 'kudos', description: '', date: new Date().toISOString().split('T')[0] });
  const [newCert, setNewCert] = useState({ name: '', issued_date: '', expiry_date: '' });

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/hr/staff/${userId}`);
      setStaff(res.data);
      setForm({
        phone: res.data.phone || '',
        address_street: res.data.address_street || '',
        address_city: res.data.address_city || '',
        address_state: res.data.address_state || '',
        address_zip: res.data.address_zip || '',
        hire_date: res.data.hire_date || '',
        department: res.data.department || '',
        job_title: res.data.job_title || '',
        employment_type: res.data.employment_type || 'full_time',
        pay_type: res.data.pay_type || 'hourly',
        pay_rate: res.data.pay_rate || '',
        pay_frequency: res.data.pay_frequency || 'biweekly',
        emergency_contact_name: res.data.emergency_contact_name || '',
        emergency_contact_phone: res.data.emergency_contact_phone || '',
        emergency_contact_relationship: res.data.emergency_contact_relationship || '',
        hr_notes: res.data.hr_notes || '',
        w4_on_file: res.data.w4_on_file || false,
        i9_on_file: res.data.i9_on_file || false,
        require_clock_in: res.data.require_clock_in !== false,
        certifications: res.data.certifications || [],
      });
    } catch (e) {
      toast.error('Failed to load staff details');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/hr/staff/${userId}`, form);
      toast.success('Profile saved');
      loadStaff();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddWriteUp = async () => {
    try {
      await api.post(`/hr/staff/${userId}/write-ups`, writeUpForm);
      toast.success('Write-up added');
      setShowWriteUpDialog(false);
      setWriteUpForm({ type: 'verbal', description: '', date: new Date().toISOString().split('T')[0] });
      loadStaff();
    } catch (e) {
      toast.error('Failed to add write-up');
    }
  };

  const handleDeleteWriteUp = async (entryId) => {
    try {
      await api.delete(`/hr/staff/${userId}/write-ups/${entryId}`);
      toast.success('Write-up removed');
      loadStaff();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const handleAddRecognition = async () => {
    try {
      await api.post(`/hr/staff/${userId}/recognitions`, recognitionForm);
      toast.success('Recognition added');
      setShowRecognitionDialog(false);
      setRecognitionForm({ type: 'kudos', description: '', date: new Date().toISOString().split('T')[0] });
      loadStaff();
    } catch (e) {
      toast.error('Failed to add recognition');
    }
  };

  const handleDeleteRecognition = async (entryId) => {
    try {
      await api.delete(`/hr/staff/${userId}/recognitions/${entryId}`);
      toast.success('Recognition removed');
      loadStaff();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const handleAddCert = () => {
    if (!newCert.name) return;
    const updated = [...(form.certifications || []), { ...newCert }];
    setForm(f => ({ ...f, certifications: updated }));
    setNewCert({ name: '', issued_date: '', expiry_date: '' });
  };

  const handleRemoveCert = (idx) => {
    const updated = form.certifications.filter((_, i) => i !== idx);
    setForm(f => ({ ...f, certifications: updated }));
  };

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  if (loading) return <div className="p-6 text-center text-gray-500">Loading...</div>;
  if (!staff) return <div className="p-6 text-center text-red-500">Staff member not found</div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="staff-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/human-resources')} data-testid="back-to-hr-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">{staff.full_name}</h1>
            <p className="text-sm text-gray-500">{staff.email}</p>
          </div>
          <Badge variant={staff.role === 'admin' ? 'default' : 'secondary'} className="capitalize">{staff.role}</Badge>
          <Badge variant={staff.is_active ? 'outline' : 'destructive'}>{staff.is_active ? 'Active' : 'Inactive'}</Badge>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
          <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="flex flex-wrap mb-4">
          <TabsTrigger value="personal" data-testid="tab-personal"><User className="h-3.5 w-3.5 mr-1" /> Personal</TabsTrigger>
          <TabsTrigger value="employment" data-testid="tab-employment"><Briefcase className="h-3.5 w-3.5 mr-1" /> Employment</TabsTrigger>
          <TabsTrigger value="compensation" data-testid="tab-compensation"><DollarSign className="h-3.5 w-3.5 mr-1" /> Compensation</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="h-3.5 w-3.5 mr-1" /> Documents</TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-records"><Shield className="h-3.5 w-3.5 mr-1" /> Records</TabsTrigger>
        </TabsList>

        {/* Personal Tab */}
        <TabsContent value="personal">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-sm"><Phone className="h-4 w-4" /> Contact</h3>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Phone</label>
                  <Input value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="(555) 123-4567" data-testid="field-phone" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Email</label>
                  <Input value={staff.email} disabled className="bg-gray-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-sm"><MapPin className="h-4 w-4" /> Address</h3>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Street</label>
                  <Input value={form.address_street} onChange={e => updateField('address_street', e.target.value)} placeholder="123 Main St" data-testid="field-street" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">City</label>
                    <Input value={form.address_city} onChange={e => updateField('address_city', e.target.value)} placeholder="City" data-testid="field-city" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">State</label>
                    <Input value={form.address_state} onChange={e => updateField('address_state', e.target.value)} placeholder="TX" data-testid="field-state" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Zip</label>
                    <Input value={form.address_zip} onChange={e => updateField('address_zip', e.target.value)} placeholder="77001" data-testid="field-zip" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-sm"><Shield className="h-4 w-4" /> Emergency Contact</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Name</label>
                    <Input value={form.emergency_contact_name} onChange={e => updateField('emergency_contact_name', e.target.value)} placeholder="Contact name" data-testid="field-ec-name" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Phone</label>
                    <Input value={form.emergency_contact_phone} onChange={e => updateField('emergency_contact_phone', e.target.value)} placeholder="(555) 123-4567" data-testid="field-ec-phone" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Relationship</label>
                    <Input value={form.emergency_contact_relationship} onChange={e => updateField('emergency_contact_relationship', e.target.value)} placeholder="Spouse, Parent, etc." data-testid="field-ec-relationship" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Employment Tab */}
        <TabsContent value="employment">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-sm"><Briefcase className="h-4 w-4" /> Job Details</h3>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Job Title</label>
                  <Input value={form.job_title} onChange={e => updateField('job_title', e.target.value)} placeholder="e.g. Senior Engineer" data-testid="field-job-title" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Department</label>
                  <Input value={form.department} onChange={e => updateField('department', e.target.value)} placeholder="e.g. Engineering" data-testid="field-department" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Employment Type</label>
                  <Select value={form.employment_type} onValueChange={v => updateField('employment_type', v)}>
                    <SelectTrigger data-testid="field-employment-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-sm"><CalendarClock className="h-4 w-4" /> Schedule & Dates</h3>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Hire Date</label>
                  <Input type="date" value={form.hire_date} onChange={e => updateField('hire_date', e.target.value)} data-testid="field-hire-date" />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <label className="text-sm">Require Clock In/Out</label>
                  <Switch checked={form.require_clock_in} onCheckedChange={v => updateField('require_clock_in', v)} data-testid="field-require-clock-in" />
                </div>
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardContent className="p-4">
                <h3 className="font-semibold flex items-center gap-2 text-sm mb-2"><FileText className="h-4 w-4" /> HR Notes</h3>
                <Textarea value={form.hr_notes} onChange={e => updateField('hr_notes', e.target.value)} placeholder="Internal notes about this staff member..." rows={3} data-testid="field-hr-notes" />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Compensation Tab */}
        <TabsContent value="compensation">
          <Card>
            <CardContent className="p-4 space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-sm"><DollarSign className="h-4 w-4" /> Pay Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Pay Type</label>
                  <Select value={form.pay_type} onValueChange={v => updateField('pay_type', v)}>
                    <SelectTrigger data-testid="field-pay-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="salary">Salary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    Rate {form.pay_type === 'hourly' ? '($/hr)' : form.pay_type === 'daily' ? '($/day)' : '($/year)'}
                  </label>
                  <Input type="number" step="0.01" value={form.pay_rate} onChange={e => updateField('pay_rate', parseFloat(e.target.value) || '')} placeholder="0.00" data-testid="field-pay-rate" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Pay Frequency</label>
                  <Select value={form.pay_frequency} onValueChange={v => updateField('pay_frequency', v)}>
                    <SelectTrigger data-testid="field-pay-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold text-sm">Tax & Employment Forms</h3>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center justify-between flex-1 p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium">W-4 Form</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={form.w4_on_file ? 'default' : 'destructive'} className="text-xs">{form.w4_on_file ? 'On File' : 'Missing'}</Badge>
                      <Switch checked={form.w4_on_file} onCheckedChange={v => updateField('w4_on_file', v)} data-testid="field-w4" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between flex-1 p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium">I-9 Form</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={form.i9_on_file ? 'default' : 'destructive'} className="text-xs">{form.i9_on_file ? 'On File' : 'Missing'}</Badge>
                      <Switch checked={form.i9_on_file} onCheckedChange={v => updateField('i9_on_file', v)} data-testid="field-i9" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold text-sm">Certifications</h3>
                {form.certifications?.length > 0 && (
                  <div className="space-y-2">
                    {form.certifications.map((cert, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 border rounded text-sm" data-testid={`cert-${idx}`}>
                        <div>
                          <span className="font-medium">{cert.name}</span>
                          {cert.issued_date && <span className="text-gray-500 ml-2">Issued: {cert.issued_date}</span>}
                          {cert.expiry_date && <span className="text-gray-500 ml-2">Expires: {cert.expiry_date}</span>}
                        </div>
                        <button onClick={() => handleRemoveCert(idx)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <Input placeholder="Certification name" value={newCert.name} onChange={e => setNewCert(c => ({...c, name: e.target.value}))} className="flex-1" data-testid="cert-name-input" />
                  <Input type="date" value={newCert.issued_date} onChange={e => setNewCert(c => ({...c, issued_date: e.target.value}))} className="w-auto" data-testid="cert-issued-input" />
                  <Input type="date" value={newCert.expiry_date} onChange={e => setNewCert(c => ({...c, expiry_date: e.target.value}))} className="w-auto" data-testid="cert-expiry-input" />
                  <Button size="sm" variant="outline" onClick={handleAddCert} disabled={!newCert.name} data-testid="add-cert-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Records Tab: Write-ups & Recognitions */}
        <TabsContent value="records">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Write-ups */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" /> Write-Ups</h3>
                  <Button size="sm" variant="outline" onClick={() => setShowWriteUpDialog(true)} data-testid="add-writeup-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add
                  </Button>
                </div>
                {(!staff.write_ups || staff.write_ups.length === 0) ? (
                  <p className="text-sm text-gray-400 py-4 text-center">No write-ups on file</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {staff.write_ups.map((wu) => (
                      <div key={wu.id} className="border rounded p-2.5 text-sm group" data-testid={`writeup-${wu.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="destructive" className="text-xs capitalize">{wu.type}</Badge>
                            <span className="text-xs text-gray-500">{wu.date}</span>
                          </div>
                          <button onClick={() => handleDeleteWriteUp(wu.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                        <p className="text-gray-700">{wu.description}</p>
                        <p className="text-xs text-gray-400 mt-1">By {wu.issued_by}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recognitions */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2"><Award className="h-4 w-4 text-amber-500" /> Recognition</h3>
                  <Button size="sm" variant="outline" onClick={() => setShowRecognitionDialog(true)} data-testid="add-recognition-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add
                  </Button>
                </div>
                {(!staff.recognitions || staff.recognitions.length === 0) ? (
                  <p className="text-sm text-gray-400 py-4 text-center">No recognitions on file</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {staff.recognitions.map((rec) => (
                      <div key={rec.id} className="border rounded p-2.5 text-sm group" data-testid={`recognition-${rec.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Badge className="text-xs capitalize bg-amber-100 text-amber-700 border-amber-200">{rec.type}</Badge>
                            <span className="text-xs text-gray-500">{rec.date}</span>
                          </div>
                          <button onClick={() => handleDeleteRecognition(rec.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                        <p className="text-gray-700">{rec.description}</p>
                        <p className="text-xs text-gray-400 mt-1">By {rec.issued_by}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Write-Up Dialog */}
      <Dialog open={showWriteUpDialog} onOpenChange={setShowWriteUpDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Write-Up</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Type</label>
              <Select value={writeUpForm.type} onValueChange={v => setWriteUpForm(f => ({...f, type: v}))}>
                <SelectTrigger data-testid="writeup-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="verbal">Verbal Warning</SelectItem>
                  <SelectItem value="written">Written Warning</SelectItem>
                  <SelectItem value="final">Final Warning</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Date</label>
              <Input type="date" value={writeUpForm.date} onChange={e => setWriteUpForm(f => ({...f, date: e.target.value}))} data-testid="writeup-date" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea value={writeUpForm.description} onChange={e => setWriteUpForm(f => ({...f, description: e.target.value}))} placeholder="Describe the incident..." rows={3} data-testid="writeup-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWriteUpDialog(false)}>Cancel</Button>
            <Button onClick={handleAddWriteUp} disabled={!writeUpForm.description} data-testid="save-writeup-btn">Save Write-Up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recognition Dialog */}
      <Dialog open={showRecognitionDialog} onOpenChange={setShowRecognitionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Recognition</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Type</label>
              <Select value={recognitionForm.type} onValueChange={v => setRecognitionForm(f => ({...f, type: v}))}>
                <SelectTrigger data-testid="recognition-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kudos">Kudos</SelectItem>
                  <SelectItem value="award">Award</SelectItem>
                  <SelectItem value="bonus">Bonus</SelectItem>
                  <SelectItem value="promotion">Promotion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Date</label>
              <Input type="date" value={recognitionForm.date} onChange={e => setRecognitionForm(f => ({...f, date: e.target.value}))} data-testid="recognition-date" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea value={recognitionForm.description} onChange={e => setRecognitionForm(f => ({...f, description: e.target.value}))} placeholder="Describe the recognition..." rows={3} data-testid="recognition-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecognitionDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRecognition} disabled={!recognitionForm.description} data-testid="save-recognition-btn">Save Recognition</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
